<template>
  <v-card ref="editDialog">
    <v-card-title tag="header" :class="$style.fixed" class="elevation-3 justify-space-between">
      <div>
        <h2 class="mb-0 w-100 mt-md-0">
          <template v-if="isCreateForm">
            {{ $t('usermanagement.form.create.modal_form_headline') }}
          </template>
          <template v-else>
            {{ $t('usermanagement.edit_user') }}
          </template>

          <strong
            >{{ currentUser.name }} <span v-if="!isCreateForm">-</span>
            {{ currentUser.data.firstName }} {{ currentUser.data.lastName }}</strong
          >
        </h2>
      </div>
    </v-card-title>
    <v-card-text>
      <v-container class="pt-10">
        <v-row>
          <v-col sm="6">
            <div ref="contactAddress" class="pb-4 mb-4 border border--b">
              <h3>
                {{ $t('usermanagement.contact_adress') }}
                <v-dialog v-model="addressIdDialog" max-width="600" scrollable persistent>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-if="isCreateForm"
                      color="primary"
                      dense
                      icon
                      :class="$style.edit"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <IconSelect icon="edit" :height="24" :width="24" />
                    </v-btn>
                  </template>

                  <v-card>
                    <v-card-title class="elevation-3 justify-space-between">
                      {{ $t('usermanagement.select_home_adress') }}
                    </v-card-title>
                    <v-card-text>
                      <DeliveryAddressSearch
                        class="pt-1 border border--b"
                        :max-height="windowHeight - 565"
                        :addresses="contactAddresses"
                        :current-address-id="currentUser.data[DP_CONTACT.KEY_ADDRESS_ID]"
                        @set-address="setTempAddressId"
                      />
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        class="my-2 btn"
                        color="secondary"
                        outlined
                        rounded
                        @click="setAddressId"
                        >{{ $t('usermanagement.set_home_adress_button') }}</v-btn
                      >
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </h3>
              <ul v-if="selectedContactAddress" :class="'unstyled-list'">
                <li v-for="adrPart in selectedContactAddress.formatted" :key="adrPart">
                  {{ adrPart }}
                </li>
              </ul>
            </div>
            <div v-if="!isCreateForm || !!currentUser?.data[DP_CONTACT.KEY_ADDRESS_ID]">
              <h3 class="mb-0">{{ $t('usermanagement.default_adress') }}</h3>
              <DeliveryAddressSearch
                v-if="deliveryAddresslistMaxHeight"
                class="border border--b"
                :max-height="deliveryAddresslistMaxHeight"
                :addresses="addressData"
                :current-address-id="currentAddressId"
                :disabled="!hasWritePermission"
                @set-address="setSelectedAddress"
              />
              <ul v-show="formErrorSelectedAddress.length > 0" class="unstyled-list">
                <li v-for="message in formErrorSelectedAddress" :key="message">{{ message }}</li>
              </ul>
            </div>
          </v-col>
          <v-col ref="fieldColumn" sm="6">
            <h3>
              {{ $t('usermanagement.basic_user_data_headline') }}
            </h3>
            <v-select
              v-if="isCreateForm"
              outlined
              :disabled="!hasWritePermission"
              :items="templateData"
              :label="$t('usermanagement.dialog.template_label')"
              :value="currentUser.sub_user_type"
              :hint="$t('usermanagement.dialog.template_change_hint')"
              class="mb-6"
              :hide-details="formErrorTemplate.length === 0"
              :error-messages="formErrorTemplate"
              v-on="getTouchListeners('currentUser.sub_user_type')"
              @change="setTemplate"
            ></v-select>
            <v-text-field
              v-model="currentUser.email"
              :error-messages="formErrorEmail"
              :label="$t('usermanagement.email')"
              outlined
              required
              class="mb-6"
              :class="$style.required"
              :disabled="!isSapSubuser || !hasWritePermission"
              :hide-details="formErrorEmail.length === 0"
              v-on="getTouchListeners('currentUser.email')"
            ></v-text-field>
            <!--<v-text-field
              v-model="currentUser.name"
              :error-messages="formErrorName"
              :hide-details="formErrorName.length === 0"
              label="Name"
              outlined
              required
              class="mb-6"
              v-on="getTouchListeners('currentUser.name')"
            ></v-text-field>-->
            <v-text-field
              v-model="currentUser.data[DP_CONTACT.KEY_FIRST_NAME]"
              :label="$t('usermanagement.firstname')"
              outlined
              class="mt-5 mb-6"
              :class="$style.required"
              required
              :disabled="!hasWritePermission"
              :error-messages="formErrorFirstName"
              :hide-details="formErrorFirstName.length === 0"
              v-on="getTouchListeners(`currentUser.data.${DP_CONTACT.KEY_FIRST_NAME}`)"
            ></v-text-field>
            <v-text-field
              v-model="currentUser.data[DP_CONTACT.KEY_LAST_NAME]"
              :label="$t('usermanagement.lastname')"
              outlined
              class="mb-6"
              :class="$style.required"
              required
              :error-messages="formErrorLastName"
              :disabled="!hasWritePermission"
              :hide-details="formErrorLastName.length === 0"
              v-on="getTouchListeners(`currentUser.data.${DP_CONTACT.KEY_LAST_NAME}`)"
            ></v-text-field>
            <v-text-field
              v-model="currentUser.data[DP_CONTACT.KEY_TELEPHONE]"
              :label="$t('usermanagement.telephone')"
              outlined
              class="mb-6 mt-15"
              :error-messages="formErrorDataTelephone"
              :disabled="!hasWritePermission"
              :hide-details="formErrorDataTelephone.length === 0"
              v-on="getTouchListeners(`currentUser.data.${DP_CONTACT.KEY_TELEPHONE}`)"
            ></v-text-field>
            <v-text-field
              v-model="currentUser.data[DP_CONTACT.KEY_MOBILE]"
              :label="$t('usermanagement.mobile')"
              outlined
              class="mb-6"
              :error-messages="formErrorDataMobile"
              :disabled="!hasWritePermission"
              :hide-details="formErrorDataMobile.length === 0"
              v-on="getTouchListeners(`currentUser.data.${DP_CONTACT.KEY_MOBILE}`)"
            ></v-text-field>
            <v-select
              v-if="!selfEdit && isSapSubuser"
              v-model="currentUser.data[DP_CONTACT.KEY_DEPARTMENT]"
              outlined
              required
              :class="$style.required"
              :disabled="!hasWritePermission"
              :items="departmentData"
              :label="$t('usermanagement.dialog.department_label')"
              :hint="$t('usermanagement.dialog.department_change_hint')"
              class="mb-6"
              :error-messages="formErrorDataDepartment"
              :hide-details="formErrorDataDepartment.length === 0"
              v-on="getTouchListeners(`currentUser.data.${DP_CONTACT.KEY_DEPARTMENT}`)"
            />
            <v-text-field
              v-model="currentUser.additional_data.reference"
              :label="$t('usermanagement.reference')"
              outlined
              hide-details
              :disabled="!hasWritePermission"
              class="mb-6 mt-15"
            ></v-text-field>
            <v-textarea
              v-model="currentUser.additional_data.openings"
              outlined
              hide-details
              name="openings"
              :disabled="!hasWritePermission"
              :label="$t('usermanagement.openings')"
              class="mb-6"
            ></v-textarea>
            <PasswordResetForm v-if="showPasswordReset" />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions class="elevation-5" :class="$style.fixed">
      <CloseChangedButton
        v-if="!forceUpdate"
        :is-changed="hasWritePermission && isChanged"
        :disabled="pending"
        @close="$emit('close')"
      />
      <v-spacer></v-spacer>
      <v-btn
        v-show="selfEdit && hasRole(ROLES.FEATURE_USERMANAGEMENT_DEBUG_R.p)"
        class="my-2 btn"
        color="secondary"
        outlined
        rounded
        @click="$emit('update-user')"
      >
        DEBUG: Update T3 user
      </v-btn>
      <span v-show="hasOtherErrors" class="error--text">{{
        $t('usermanagement.dialog.saveError')
      }}</span>
      <v-spacer></v-spacer>
      <v-btn
        v-show="hasWritePermission"
        class="my-2 btn"
        color="secondary"
        outlined
        rounded
        :disabled="pending || !isChanged"
        @click="save"
      >
        {{
          isCreateForm
            ? $t('usermanagement.form.create.modal_form_headline')
            : $t('usermanagement.save')
        }}
      </v-btn>
    </v-card-actions>
    <LoaderElement v-if="pending" color="primary" :loading="pending" :size="128" />
  </v-card>
</template>

<script>
import { cloneDeep, isEqual, get, has, sortBy, omit, isEmpty } from 'lodash-es'
import { objectDiff } from '@/js/helper/object-diff-helper.js'
import { mapGetters } from 'vuex'
import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
import APP_GETTERS from '@/js/plugins/store/modules/app/getter-types.js'

import { required, email } from 'vuelidate/lib/validators'
import { phoneNumber } from '@/js/components/Forms/helper/form-validation-helper.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import PermissionMixin from '@/js/mixins/permission-mixin.js'

import PasswordResetForm from '@/js/components/Account/UserManagement/forms/PasswordResetForm.vue'

import { ReactiveRefs } from 'vue-reactive-refs'
import Vue from 'vue'
Vue.use(ReactiveRefs)

import DeliveryAddressSearch from '@/js/components/Checkout/Address/DeliveryAddressSearch.vue'
import CloseChangedButton from '@/js/components/Shared/CloseChangedButton.vue'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'
import LoaderElement from '@/js/components/Shared/LoaderElement.vue'
// import UsermanagementTemplate from '@/js/components/Usermanagement/UsermanagementTemplate.vue'

import { DP_CONTACT, DP_PERM } from '@/js/helper/dp-usermanagement-constants.js'
import { formatDeliveryAddress } from '@/js/helper/aimeos-basket-address-helper.js'

const userDefault = {
  data: {
    partnerFunction: DP_CONTACT.PARTNER_FUNCTION_DEFAULT,
  },
  additional_data: {},
}

export default {
  name: 'UsermanagementEdit',
  refs: ['fieldColumn', 'contactAddress'],
  components: {
    DeliveryAddressSearch,
    CloseChangedButton,
    IconSelect,
    LoaderElement,
    PasswordResetForm,
    // UsermanagementTemplate,
  },
  mixins: [PermissionMixin, FormValidationMixin],
  props: {
    selfEdit: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: () => cloneDeep(userDefault),
    },
    addresses: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: [Object],
      default: () => ({}),
    },
    pending: {
      type: Boolean,
      default: false,
    },
    showPasswordReset: {
      type: Boolean,
      default: false,
    },
    forceUpdate: {
      type: Boolean,
      default: false,
    },
  },
  validations() {
    const validations = {
      currentUser: {
        // name: {
        //   required,
        //   responseError: this.getResponseError('name'),
        // },
        sub_user_type: {
          responseError: this.getResponseError('sub_user_type'),
        },
        email: {
          required,
          responseError: this.getResponseError('email'),
          email,
          // usermanagementEmailInUseError: function (value) {
          //   return !this.usedEmails.includes(value)
          // },
        },
        data: {
          firstName: {
            required,
            responseError: this.getResponseError(`data.${DP_CONTACT.KEY_FIRST_NAME}`),
          },
          lastName: {
            required,
            responseError: this.getResponseError(`data.${DP_CONTACT.KEY_LAST_NAME}`),
          },
          telephone: {
            phoneNumber,
            responseError: this.getResponseError(`data.${DP_CONTACT.KEY_TELEPHONE}`),
          },
          mobile: {
            phoneNumber,
            responseError: this.getResponseError(`data.${DP_CONTACT.KEY_MOBILE}`),
          },
          department: {
            responseError: this.getResponseError(`data.${DP_CONTACT.KEY_DEPARTMENT}`),
          },
        },
        additional_data: {},
      },
    }
    if (!this.selfEdit && this.isSapSubuser) {
      validations.currentUser.data.department.required = required
    }
    return validations
  },
  data() {
    return {
      DP_CONTACT,
      currentUser: undefined,
      addressIdDialog: false,
      tempAddressId: '',
      deliveryAddresslistMaxHeight: undefined,
    }
  },
  computed: {
    ...mapGetters(STOREMODULES.APP, {
      windowHeight: APP_GETTERS.GET_HEIGHT,
    }),
    hasWritePermission() {
      return this.selfEdit
        ? this.hasRole(this.ROLES.ACCOUNT_USER_SELF_W.p)
        : this.hasRole(this.ROLES.ACCOUNT_USER_SUBUSER_W.p)
    },
    isSapSubuser() {
      return !DP_PERM.SAP_EXCLUDE_TEMPLATES.includes(this.currentUser?.sub_user_type)
    },
    refFieldCol() {
      return get(this.$refs, 'fieldColumn')
    },
    refContactAdrCol() {
      return get(this.$refs, 'contactAddress')
    },
    // adressListMaxHeight() {
    //   return this.$refs.fieldColumn?.clientHeight
    // },
    isCreateForm() {
      return isEqual(this.user, userDefault)
    },
    formErrorTemplate() {
      return this.formErrorHelper('currentUser.sub_user_type', [this.responseTemplateError])
    },
    // responseNameError() {
    //   const nameError = this.errors?.name
    //   return nameError
    //     ? {
    //         condition: 'responseError',
    //         error: nameError.includes('The name has already been taken.')
    //           ? this.$t('usermanagement.field-username-isUsed')
    //           : this.$t('form.error.name'),
    //       }
    //     : this.requiredError
    // },
    otherErrors() {
      const errorFields = [
        `data.${DP_CONTACT.KEY_FIRST_NAME}`,
        `data.${DP_CONTACT.KEY_LAST_NAME}`,
        `data.${DP_CONTACT.KEY_MOBILE}`,
        `data.${DP_CONTACT.KEY_TELEPHONE}`,
        `data.${DP_CONTACT.KEY_DEPARTMENT}`,
        'sub_user_type',
        'selected_address',
        'email',
      ]
      return omit(this.errors, errorFields)
    },
    hasOtherErrors() {
      return !isEmpty(this.otherErrors)
    },
    responseEmailError() {
      const emailError = this.errors?.email
      return emailError
        ? {
            condition: 'responseError',
            error: emailError.includes('The email has already been taken.')
              ? this.$t('usermanagement.field-email-isUsed')
              : this.$t('form.error.email'),
          }
        : this.requiredError
    },
    responseSelectedAddressError() {
      const error = this.errors?.selected_address
      return error
        ? {
            condition: 'responseError',
            error: this.$t('usermanagement.error.selected_address'),
          }
        : null
    },
    responseTemplateError() {
      const error = this.errors?.sub_user_type
      return error
        ? {
            condition: 'responseError',
            error: this.$t('usermanagement.error.template'),
          }
        : null
    },
    responseFirstNameError() {
      const error = this.errors?.[`data.${DP_CONTACT.KEY_FIRST_NAME}`]
      return error
        ? {
            condition: 'responseError',
            error: this.$t('usermanagement.error.firstName'),
          }
        : this.requiredError
    },
    responseLastNameError() {
      const error = this.errors?.[`data.${DP_CONTACT.KEY_LAST_NAME}`]
      return error
        ? {
            condition: 'responseError',
            error: this.$t('usermanagement.error.lastName'),
          }
        : this.requiredError
    },
    responseMobileError() {
      const error = this.errors?.[`data.${DP_CONTACT.KEY_MOBILE}`]
      return error
        ? {
            condition: 'responseError',
            error: this.$t('usermanagement.error.mobile'),
          }
        : null
    },
    responseTelephoneError() {
      const error = this.errors?.[`data.${DP_CONTACT.KEY_TELEPHONE}`]
      return error
        ? {
            condition: 'responseError',
            error: this.$t('usermanagement.error.telephone'),
          }
        : null
    },
    responseDepartmentError() {
      const error = this.errors?.[`data.${DP_CONTACT.KEY_DEPARTMENT}`]
      return error
        ? {
            condition: 'responseError',
            error: this.$t('usermanagement.error.department'),
          }
        : this.requiredError
    },
    // formErrorName() {
    //   return this.formErrorHelper('currentUser.name', [this.requiredError, this.responseNameError])
    // },
    formErrorFirstName() {
      return this.formErrorHelper(`currentUser.data.${DP_CONTACT.KEY_FIRST_NAME}`, [
        this.responseFirstNameError,
      ])
    },
    formErrorLastName() {
      return this.formErrorHelper(`currentUser.data.${DP_CONTACT.KEY_LAST_NAME}`, [
        this.requiredError,
        this.responseLastNameError,
      ])
    },
    formErrorMobile() {
      return this.formErrorHelper(`currentUser.data.${DP_CONTACT.KEY_MOBILE}`, [
        this.responseMobileError,
      ])
    },
    formErrorSelectedAddress() {
      return this.formErrorHelper('currentUser.selected_address', [
        this.responseSelectedAddressError,
      ])
    },
    formErrorEmail() {
      return this.formErrorHelper('currentUser.email', [
        this.requiredError,
        this.responseEmailError,
        this.emailError,
      ])
    },
    formErrorDataTelephone() {
      return this.formErrorHelper(`currentUser.data.${DP_CONTACT.KEY_TELEPHONE}`, [
        this.phoneError,
        this.responseTelephoneError,
      ])
    },
    formErrorDataMobile() {
      return this.formErrorHelper(`currentUser.data.${DP_CONTACT.KEY_MOBILE}`, [
        this.phoneError,
        this.responseMobileError,
      ])
    },
    formErrorDataDepartment() {
      return this.formErrorHelper(`currentUser.data.${DP_CONTACT.KEY_DEPARTMENT}`, [
        this.responseDepartmentError,
      ])
    },
    currentAddressId() {
      return this.currentUser?.selected_address
    },
    isChanged() {
      return !isEqual(this.user, this.currentUser)
    },
    changes() {
      return objectDiff(this.currentUser, this.user)
    },
    templateData() {
      return [
        // usermanagement.templates.fullaccess
        // usermanagement.templates.buchhaltung
        // usermanagement.templates._oktauser
        // usermanagement.templates.verkauf_einkauf
        // usermanagement.templates.werkstatt
        { value: '', text: this.$t('usermanagement.templates.empty') },
        ...this.templates.map(template => ({
          value: template.key,
          text: this.$t(`usermanagement.templates.${template.key}`),
        })),
      ]
    },
    departmentData() {
      return [
        // { value: '', text: this.$t('usermanagement.departments.empty') },
        // usermanagement.departments.0001
        // usermanagement.departments.0002
        // usermanagement.departments.0003
        // usermanagement.departments.0009
        // usermanagement.departments.0011
        // usermanagement.departments.0012
        // usermanagement.departments.0013
        // usermanagement.departments.0014
        // usermanagement.departments.0015
        ...DP_CONTACT.DEPARTMENT_VALUES.map(department => ({
          value: department,
          text: this.$t(`usermanagement.departments.${department}`),
        })),
      ]
    },
    contactAddresses() {
      const paymentAddress = this.addresses.find(adr => adr.type === 'payment')
      const deliveryAddressesHavePaymentAdressId = paymentAddress?.address_id
        ? !!this.deliveryAddresses.find(adr => adr.address_id === paymentAddress.address_id)
        : false
      console.log({ paymentAddress }, deliveryAddressesHavePaymentAdressId)
      const contactAddresses = deliveryAddressesHavePaymentAdressId
        ? this.deliveryAddresses
        : [
            { ...paymentAddress, sap_address_id: paymentAddress.address_id },
            ...this.deliveryAddresses,
          ]
      return (
        this.currentUser?.data?.addressId
          ? sortBy(
              contactAddresses,
              adr => adr.address_id !== this.currentUser.data?.[DP_CONTACT.KEY_ADDRESS_ID],
            )
          : contactAddresses
      ).map(adr => {
        return formatDeliveryAddress(
          {
            id: adr.address_id,
            attributes: adr.content,
          },
          this.currentUser.name,
        )
      })
    },
    selectedContactAddress() {
      return this.contactAddresses.find(
        adr => adr.id === this.currentUser.data?.[DP_CONTACT.KEY_ADDRESS_ID],
      )
    },
    deliveryAddresses() {
      return this.addresses.filter(adr => adr.type === 'delivery')
    },
    addressData() {
      return (
        this.currentUser?.data?.[DP_CONTACT.KEY_ADDRESS_ID]
          ? sortBy(this.deliveryAddresses, adr => {
              // console.log(
              //   'addressData',
              //   adr,
              //   adr.content?.contact_person.includes(this.currentUser.name),
              // )
              return adr.address_id !== this.currentUser.data?.[DP_CONTACT.KEY_ADDRESS_ID]
            })
          : this.deliveryAddresses
      ).map(adr => {
        return formatDeliveryAddress(
          {
            id: adr.address_id,
            attributes: adr.content,
          },
          this.currentUser?.data?.addressId,
        )
      })
    },
    // addressData() {
    //   return (
    //     this.currentUser.name
    //       ? sortBy(this.addresses, adr => {
    //           console.log(
    //             'addressData',
    //             adr,
    //             adr.content?.contact_person.includes(this.currentUser.name),
    //           )
    //           return adr.content?.contact_person.includes(this.currentUser.name) ? 0 : 1
    //         })
    //       : this.addresses
    //   ).map(adr => {
    //     return formatDeliveryAddress(
    //       {
    //         id: adr.address_id,
    //         attributes: adr.content,
    //       },
    //       this.currentUser.name,
    //     )
    //   })
    // },
  },
  watch: {
    user: {
      handler() {
        this.currentUser = cloneDeep(this.user)
      },
      immediate: true,
    },
    errors() {
      this.validate()
    },
    refContactAdrCol() {
      this.setDeliveryAddresslistMaxHeight()
    },
    refFieldCol() {
      this.setDeliveryAddresslistMaxHeight()
    },
  },
  mounted() {
    this.addressIdDialog = this.isCreateForm && !this.currentUser.data[DP_CONTACT.KEY_ADDRESS_ID]

    this.forceUpdate ? this.validate() : null
  },
  methods: {
    // Overwrite touch method from form-validation-mixin
    touch(name) {
      get(this.$v, name).$touch()
      const key = name.substring(12) //remove "currentUser."
      if (has(this.errors, key)) {
        this.$delete(this.errors, key)
      }
    },
    getResponseError(field) {
      return () => {
        // return (value) => {
        // console.log('getResponseError', value, field, this.errors, !has(this.errors, field))
        return !has(this.errors, field)
      }
    },
    setSelectedAddress(sapId) {
      this.currentUser.selected_address = sapId
    },
    setTemplate(template) {
      // ToDo: show confirm dialog
      this.$set(this.currentUser, 'sub_user_type', template)
      // ToDo: set groups according to template
    },
    setTempAddressId(tempId) {
      this.tempAddressId = tempId
    },
    setAddressId() {
      this.$set(this.currentUser.data, DP_CONTACT.KEY_ADDRESS_ID, this.tempAddressId)
      this.$set(this.currentUser, 'selected_address', this.tempAddressId)
      this.addressIdDialog = false
    },
    save() {
      this.validate()

      if (!this.isValid) {
        return
      }

      if (this.isCreateForm) {
        this.$emit('create', omit(this.currentUser, 'user_groups')) // We do not edit user_groups here…
        return
      }
      const userData = omit(this.currentUser, ['user_groups', 'sub_user_type'])
      if (!this.isSapSubuser) {
        userData.data = omit(userData.data, [DP_CONTACT.KEY_DEPARTMENT])
      }
      this.$emit('save', userData)
    },
    setDeliveryAddresslistMaxHeight() {
      this.$nextTick(() => {
        setTimeout(() => {
          const refFieldColHeight = this.refFieldCol
            ? this.refFieldCol.getBoundingClientRect()
            : undefined
          const refContactAdrColHeight = this.refContactAdrCol
            ? this.refContactAdrCol.getBoundingClientRect()
            : undefined
          const deliveryAddresslistMaxHeight =
            refFieldColHeight && refContactAdrColHeight
              ? refFieldColHeight.height - refContactAdrColHeight.height - 16 - 30 - 82 - 50
              : undefined
          if (!deliveryAddresslistMaxHeight) {
            this.setDeliveryAddresslistMaxHeight()
          }
          if (this.deliveryAddresslistMaxHeight === deliveryAddresslistMaxHeight) {
            return
          }
          this.deliveryAddresslistMaxHeight = deliveryAddresslistMaxHeight
          this.setDeliveryAddresslistMaxHeight()
        }, 100)
      })
    },
  },
}
</script>

<style lang="scss" module>
@keyframes highlighter {
  0%,
  100% {
    opacity: 0;
  }

  50% {
    opacity: 0.2;
  }
}

.fixed {
  z-index: 2;
}

.required :global(.v-label)::after {
  color: var(--error);

  content: ' *';
}

/************************
RESPONSIVE
*************************/

.stickyHeader {
  position: sticky;
  top: 0;
  z-index: 9;
}

.stickyHeader:after {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;

  width: 100%;
  height: calc(100% - 1px);

  background: #fff;
  box-shadow: 0 1px 0 var(--grey-lighten-2);

  content: '';
}

mark {
  background-color: var(--v-warning-base);
}

.highlighter:after {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9;

  width: 100%;
  height: 100%;

  background: var(--grey-disabled);
  opacity: 0;

  animation: highlighter 3 0.4s;

  content: '';
  pointer-events: none;
}

.bgGray {
  background-color: var(--grey-disabled) !important;
}

.edit {
  flex: 0 1 2rem;
  min-width: 2.5rem;
  max-width: 2.5rem;
  min-height: 2.5rem;
  max-height: 2.5rem;
  margin-right: 0.5rem;
}
</style>
